/**
 * Admin page: at-a-glance view of what build this server runs and in which environment.
 */

import type { ReactNode } from 'react';
import { Alert, Box, CircularProgress, Paper, Typography } from '@mui/material';
import { ApiClientError } from '../api/client';
import { useAdminStatus } from '../hooks/useAdminStatus';
import { usePageTitle } from '../hooks/usePageTitle';
import { useMe } from '../hooks/useUsers';

function StatRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.75 }}>
      <Typography color="text.secondary">{label}</Typography>
      <Typography sx={{ fontWeight: 500, textAlign: 'right', wordBreak: 'break-word' }}>{children}</Typography>
    </Box>
  );
}

function Forbidden() {
  return (
    <Box sx={{ p: 2 }}>
      <Alert severity="warning" data-testid="admin-forbidden">
        You don&apos;t have permission to view the Admin page.
      </Alert>
    </Box>
  );
}

export default function AdminPage() {
  usePageTitle('Admin');
  const { data: me } = useMe();
  if (!me) return null;
  if (!me.permissions.includes('VIEW_ADMIN')) return <Forbidden />;
  return <BuildInfo />;
}

/** Reads `GET /api/v1/admin`; rendered only for a caller holding `VIEW_ADMIN`. */
function BuildInfo() {
  const { data, error, isLoading } = useAdminStatus();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error instanceof ApiClientError && error.status === 403) return <Forbidden />;
  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" data-testid="admin-error">
          Error: {error.message}
        </Alert>
      </Box>
    );
  }
  if (!data) return null;

  return (
    <Box sx={{ p: 2, maxWidth: 560 }} data-testid="admin-page">
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" component="h1" gutterBottom>
          Build Info
        </Typography>
        <StatRow label="Environment">{data.environment}</StatRow>
        <StatRow label="Version">{data.version}</StatRow>
        <StatRow label="Commit">{data.commitSha}</StatRow>
        <StatRow label="Built">{data.buildTime}</StatRow>
      </Paper>
    </Box>
  );
}
